import { useEffect, useState } from 'react';
import axios from 'axios';
import { Download, RefreshCw } from 'lucide-react';
import CRMLayout from '../../components/CRM/CRMLayout';
import { Button } from '../../components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const RATE = (value) => `${Number(value || 0).toFixed(2)}%`;

export default function ApplyAnalyticsDashboard() {
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);

  const fetchAnalytics = async (selectedDays = days) => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(`${API}/admin/analytics/apply-funnel`, {
        params: { days: selectedDays },
      });
      setData(response.data);
    } catch (fetchError) {
      setError(fetchError?.response?.data?.detail || 'Failed to load apply analytics.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalytics(days);
  }, [days]);

  const handleExportCsv = async () => {
    setExporting(true);
    try {
      const response = await axios.get(`${API}/admin/analytics/apply-funnel/export`, {
        params: { days },
        responseType: 'blob',
      });
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `apply-funnel-events-last-${days}-days.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (exportError) {
      setError(exportError?.response?.data?.detail || 'CSV export failed.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <CRMLayout>
      <div className="min-h-screen bg-navy-900 text-white pb-20" data-testid="admin-analytics-page">
        <header className="border-b border-white/10 bg-navy-950/70 px-6 py-6" data-testid="admin-analytics-header">
          <h1 className="text-3xl font-heading uppercase tracking-wide" data-testid="admin-analytics-heading">
            Apply Funnel Analytics
          </h1>
          <p className="text-sm text-white/70 mt-2" data-testid="admin-analytics-subheading">
            Internal-only step funnel visibility for the /apply journey.
          </p>
        </header>

        <section className="px-6 py-6" data-testid="admin-analytics-controls-section">
          <div className="flex flex-wrap items-center gap-3" data-testid="admin-analytics-controls-row">
            <label htmlFor="admin-analytics-days" className="text-sm text-white/80" data-testid="admin-analytics-days-label">
              Window
            </label>
            <select
              id="admin-analytics-days"
              value={days}
              onChange={(event) => setDays(Number(event.target.value))}
              className="h-10 rounded-lg border border-purple-500/30 bg-navy-800 px-3 text-sm"
              data-testid="admin-analytics-days-select"
            >
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
              <option value={180}>Last 180 days</option>
            </select>

            <Button
              type="button"
              variant="outline"
              className="border-purple-500/40 bg-navy-800 text-white hover:bg-purple-500/15"
              onClick={() => fetchAnalytics(days)}
              data-testid="admin-analytics-refresh-button"
            >
              <RefreshCw size={15} className="mr-2" /> Refresh
            </Button>

            <Button
              type="button"
              className="bg-purple-600 hover:bg-purple-500 text-white"
              onClick={handleExportCsv}
              disabled={exporting}
              data-testid="admin-analytics-export-csv-button"
            >
              <Download size={15} className="mr-2" /> {exporting ? 'Exporting...' : 'Export CSV'}
            </Button>
          </div>
        </section>

        <section className="px-6" data-testid="admin-analytics-content-section">
          {loading ? (
            <div className="rounded-xl border border-white/10 bg-navy-800/60 p-10 text-center" data-testid="admin-analytics-loading">
              Loading analytics...
            </div>
          ) : error ? (
            <div className="rounded-xl border border-red-500/30 bg-red-900/20 p-6 text-red-200" data-testid="admin-analytics-error">
              {error}
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6" data-testid="admin-analytics-summary-grid">
                <article className="rounded-xl border border-purple-500/30 bg-navy-800/70 p-5" data-testid="admin-analytics-card-started">
                  <p className="text-xs uppercase tracking-[0.14em] text-purple-200">Applications Started</p>
                  <p className="text-3xl font-bold mt-2" data-testid="admin-analytics-started-value">{data?.totals?.applications_started || 0}</p>
                </article>
                <article className="rounded-xl border border-emerald-500/30 bg-navy-800/70 p-5" data-testid="admin-analytics-card-completed">
                  <p className="text-xs uppercase tracking-[0.14em] text-emerald-200">Applications Completed</p>
                  <p className="text-3xl font-bold mt-2" data-testid="admin-analytics-completed-value">{data?.totals?.applications_completed || 0}</p>
                </article>
                <article className="rounded-xl border border-blue-500/30 bg-navy-800/70 p-5" data-testid="admin-analytics-card-completion-rate">
                  <p className="text-xs uppercase tracking-[0.14em] text-blue-200">Overall Completion Rate</p>
                  <p className="text-3xl font-bold mt-2" data-testid="admin-analytics-completion-rate-value">{RATE(data?.totals?.overall_completion_rate_pct)}</p>
                </article>
              </div>

              <div className="rounded-xl border border-white/10 bg-navy-800/70 overflow-x-auto" data-testid="admin-analytics-step-table-wrap">
                <table className="w-full min-w-[760px]" data-testid="admin-analytics-step-table">
                  <thead>
                    <tr className="border-b border-white/10 text-left text-xs uppercase tracking-[0.12em] text-white/65">
                      <th className="px-4 py-3" data-testid="admin-analytics-col-step">Step</th>
                      <th className="px-4 py-3" data-testid="admin-analytics-col-reached">Reached</th>
                      <th className="px-4 py-3" data-testid="admin-analytics-col-advanced">Advanced</th>
                      <th className="px-4 py-3" data-testid="admin-analytics-col-back">Back Events</th>
                      <th className="px-4 py-3" data-testid="admin-analytics-col-abandoned">Abandoned</th>
                      <th className="px-4 py-3" data-testid="admin-analytics-col-completion-rate">Completion Rate</th>
                      <th className="px-4 py-3" data-testid="admin-analytics-col-dropoff-rate">Drop-off Rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data?.steps || []).map((step) => (
                      <tr key={step.step_key} className="border-b border-white/5 text-sm" data-testid={`admin-analytics-step-row-${step.step_key}`}>
                        <td className="px-4 py-3 font-medium" data-testid={`admin-analytics-step-label-${step.step_key}`}>{step.step_label}</td>
                        <td className="px-4 py-3" data-testid={`admin-analytics-step-reached-${step.step_key}`}>{step.sessions_reached}</td>
                        <td className="px-4 py-3" data-testid={`admin-analytics-step-advanced-${step.step_key}`}>{step.sessions_advanced}</td>
                        <td className="px-4 py-3" data-testid={`admin-analytics-step-back-${step.step_key}`}>{step.back_events}</td>
                        <td className="px-4 py-3" data-testid={`admin-analytics-step-abandoned-${step.step_key}`}>{step.sessions_abandoned}</td>
                        <td className="px-4 py-3" data-testid={`admin-analytics-step-completion-${step.step_key}`}>{RATE(step.completion_rate_pct)}</td>
                        <td className="px-4 py-3" data-testid={`admin-analytics-step-dropoff-${step.step_key}`}>{RATE(step.dropoff_rate_pct)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </section>
      </div>
    </CRMLayout>
  );
}
